import React, { useMemo, useState } from "react";
import { CalendarPlus, RotateCw } from "lucide-react";
import { toast } from "sonner";
import { finApi, finErr, finPref, money } from "@/lib/financeApi";
import { btnGold, Chip, Empty, ErrorBox, field, Loading, OBLIGATION_CHIP, StatusFilter, SummaryChip, useLoad } from "@/components/nds/windows/finance/parts";
import { useIsSuper } from "@/components/nds/windows/finance/InvoiceActions";
import ObligationActions from "@/components/nds/windows/finance/ObligationActions";
import ObligationDetail from "@/components/nds/windows/finance/ObligationDetail";
import { OpenResidentButton } from "@/components/nds/OpenResidentButton";
import { ListTools, sortRows } from "@/components/nds/ListTools";

const OGET = { name: (r) => r.resident_name, date: (r) => r.period_start, owed: (r) => r.balance_due };

function GenerateForm({ cfg, onDone, onCancel }) {
  const [f, setF] = useState({ period_start: "", period_end: "", amount_due: "", fee_type: cfg.feeTypes?.[0]?.[0] });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF((x) => ({ ...x, [k]: e.target.value }));
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const body = { ...f, amount_due: Number(f.amount_due) };
      if (!cfg.feeTypes) delete body.fee_type;
      const r = await finApi.post(`${cfg.api}/generate`, body);
      toast.success(`Generated ${r.created} record(s) · skipped ${r.skipped} existing`);
      onDone(f.period_start);
    } catch (err) { toast.error(finErr(err)); } finally { setBusy(false); }
  };
  return (
    <form onSubmit={submit} data-testid={`${cfg.testid}-gen-form`} className="p-4 border border-nex-gold/30 rounded-sm bg-white/[0.02] flex flex-wrap items-center gap-2">
      <span className="font-mono text-[10px] tracking-[0.2em] uppercase text-nex-muted">Generate for all active residents</span>
      <input type="date" required value={f.period_start} onChange={set("period_start")} data-testid={`${cfg.testid}-gen-start`} className={field} />
      <input type="date" required value={f.period_end} onChange={set("period_end")} data-testid={`${cfg.testid}-gen-end`} className={field} />
      <input type="number" required min="0.01" step="0.01" value={f.amount_due} onChange={set("amount_due")} placeholder="Amount due" data-testid={`${cfg.testid}-gen-amount`} className={`${field} w-32`} />
      {cfg.feeTypes && (
        <select value={f.fee_type} onChange={set("fee_type")} data-testid={`${cfg.testid}-gen-fee-type`} className={field}>
          {cfg.feeTypes.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
        </select>
      )}
      <button type="button" onClick={onCancel} data-testid={`${cfg.testid}-gen-cancel`} className="ml-auto font-mono text-[10px] tracking-[0.2em] uppercase text-nex-muted">Cancel</button>
      <button type="submit" disabled={busy} data-testid={`${cfg.testid}-gen-submit`} className={btnGold}>Generate</button>
    </form>
  );
}

/** Shared roster for Rent Roll and Program Fees (cfg: api, testid, label, summaryTone, summaryText, feeTypes, refType). */
export default function ObligationContent({ cfg }) {
  const isSuper = useIsSuper();
  const [status, setStatusState] = useState(() => finPref.get(`${cfg.key}_status`, "all"));
  const [period, setPeriodState] = useState(() => finPref.get(`${cfg.key}_period`, ""));
  const [generating, setGenerating] = useState(false);
  const [selected, setSelected] = useState(null);
  const [q, setQ] = useState("");
  const [sort, setSort] = useState("urgent");
  const setStatus = (v) => { finPref.set(`${cfg.key}_status`, v); setStatusState(v); };
  const setPeriod = (v) => { finPref.set(`${cfg.key}_period`, v); setPeriodState(v); };
  const [state, load] = useLoad(() => Promise.all([finApi.get(cfg.api), finApi.get(`${cfg.api}/summary`)])
    .then(([l, s]) => ({ records: l.records, summary: s })), [cfg.api]);
  const periods = useMemo(() => [...new Set((state.data?.records || []).map((r) => r.period_start))].sort().reverse(), [state.data]);

  if (selected) return <ObligationDetail cfg={cfg} recordId={selected} onBack={() => { setSelected(null); load(); }} />;
  if (state.loading && !state.data) return <Loading testid={`${cfg.testid}-loading`} label={`Loading ${cfg.label.toLowerCase()}…`} />;
  if (state.error) return <ErrorBox testid={`${cfg.testid}-error`} error={state.error} onRetry={load} />;
  const inPeriod = state.data.records.filter((r) => !period || r.period_start === period);
  const counts = { all: inPeriod.length };
  inPeriod.forEach((r) => { counts[r.status] = (counts[r.status] || 0) + 1; });
  const list = sortRows(status === "all" ? inPeriod : inPeriod.filter((r) => r.status === status), OGET, sort, q);

  return (
    <div data-testid={`${cfg.testid}-content`} className="flex flex-col h-full">
      <div className="px-5 py-3 border-b border-white/10 space-y-2">
        <div className="flex flex-wrap items-center gap-2">
          <SummaryChip tone={cfg.summaryTone} testid={`${cfg.testid}-summary`}>{cfg.summaryText(state.data.summary)}</SummaryChip>
          <select value={period} onChange={(e) => setPeriod(e.target.value)} data-testid={`${cfg.testid}-period`} aria-label="Period" className={`${field} !py-1 ml-auto`}>
            <option value="">All periods</option>
            {periods.map((p) => <option key={p} value={p}>{p}</option>)}
          </select>
          {isSuper && !generating && <button type="button" onClick={() => setGenerating(true)} data-testid={`${cfg.testid}-generate`} className={`${btnGold} inline-flex items-center gap-1.5`}><CalendarPlus className="h-3 w-3" /> Generate period</button>}
          <button type="button" onClick={load} data-testid={`${cfg.testid}-refresh`} aria-label="Refresh" className="p-1.5 text-nex-muted hover:text-nex-gold"><RotateCw className="h-3.5 w-3.5" /></button>
        </div>
        <StatusFilter prefix={cfg.testid} chips={OBLIGATION_CHIP} value={status} onChange={setStatus} counts={counts} />
        <ListTools prefix={cfg.testid} q={q} setQ={setQ} sort={sort} setSort={setSort} get={OGET} />
      </div>
      {generating && <div className="px-5 pt-3"><GenerateForm cfg={cfg} onCancel={() => setGenerating(false)} onDone={(p) => { setGenerating(false); setPeriod(p); load(); }} /></div>}
      {list.length ? (
        <ul data-testid={`${cfg.testid}-list`} className="flex-1 overflow-y-auto divide-y divide-white/5">
          {list.map((r) => {
            const [tone, l] = OBLIGATION_CHIP[r.status];
            return (
              <li key={r.id} data-testid={`${cfg.testid}-row-${r.id}`} onClick={() => setSelected(r.id)} className="px-5 py-3 space-y-2 cursor-pointer hover:bg-nex-gold/5 transition-colors">
                <div className="flex flex-wrap items-center gap-3">
                  <span className="text-nex-silver font-medium w-44 truncate">{r.resident_name}</span>
                  <OpenResidentButton residentId={r.resident_id} name={r.resident_name} testid={`open-resident-obl-${r.id}`} />
                  <span className="font-mono text-[10px] text-nex-muted">{r.period_start} → {r.period_end}</span>
                  {cfg.feeTypes && <Chip tone="purple" data-testid={`${cfg.testid}-type-${r.id}`}>{(cfg.feeTypes.find((x) => x[0] === r.fee_type) || [, r.fee_type])[1]}</Chip>}
                  <Chip tone={tone} data-testid={`${cfg.testid}-status-${r.id}`}>{l}</Chip>
                  <span className="ml-auto font-mono text-[10px] text-nex-muted">due {money(r.amount_due)} · paid {money(r.amount_paid)}</span>
                  <span data-testid={`${cfg.testid}-balance-${r.id}`} className={`w-24 text-right font-mono text-sm ${r.balance_due > 0 ? "text-red-300" : "text-nex-muted"}`}>{money(r.balance_due)}</span>
                </div>
                <ObligationActions cfg={cfg} rec={r} onChanged={load} />
              </li>
            );
          })}
        </ul>
      ) : <Empty testid={`${cfg.testid}-empty`}>No {cfg.label.toLowerCase()} records{period ? ` for ${period}` : ""}{status !== "all" ? ` with status ${status}` : ""}.</Empty>}
    </div>
  );
}
